import React, { useState, useEffect } from "react";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Crown, Sparkles, Loader2 } from "lucide-react";

/**
 * Small "Boost to Premium" button rendered on each user's own property card.
 * Opens a modal with Monthly (₹299) or Yearly (₹999) choice.
 * If property is already premium, shows a green "Premium ✓" pill with expiry.
 */
export default function BoostPremiumButton({ property, onBoosted }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [rzpReady, setRzpReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (window.Razorpay) { setRzpReady(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => setRzpReady(true);
    s.onerror = () => setRzpReady(false);
    document.body.appendChild(s);
  }, [open]);

  const isPremium = !!property.is_premium;
  const expiry = property.premium_expires_at
    ? new Date(property.premium_expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : null;

  const boost = async (planKey) => {
    setProcessing(planKey);
    try {
      const { data: order } = await api.post(`/properties/${property.id}/premium/order`, { plan: planKey });
      if (order.mock) {
        const { data: verified } = await api.post(`/properties/${property.id}/premium/verify`, {
          order_id: order.order_id,
          razorpay_payment_id: `MOCK_PAY_${Date.now()}`,
          razorpay_signature: "mock-signature",
        });
        toast.success(lang === "hi" ? "मॉक भुगतान सफल — प्रॉपर्टी बूस्ट हुई!" : "Mock payment success — property boosted!");
        setOpen(false);
        onBoosted?.(verified);
        return;
      }
      if (!window.Razorpay) {
        toast.error("Payment system loading — try again in a moment.");
        return;
      }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "BhoomiMitra",
        description: `Premium Boost — ${property.title}`,
        order_id: order.order_id,
        prefill: { name: user?.name || "", email: user?.email, contact: user?.phone || "" },
        theme: { color: "#B84A2E" },
        handler: async (rzpRes) => {
          try {
            const { data: verified } = await api.post(`/properties/${property.id}/premium/verify`, {
              order_id: rzpRes.razorpay_order_id,
              razorpay_payment_id: rzpRes.razorpay_payment_id,
              razorpay_signature: rzpRes.razorpay_signature,
            });
            toast.success(lang === "hi" ? "बूस्ट सफल! अब होमपेज पर दिखेगी।" : "Boost successful! Now on Homepage.");
            setOpen(false);
            onBoosted?.(verified);
          } catch (e) {
            toast.error(formatDetail(e.response?.data?.detail) || "Verification failed");
          }
        },
        modal: { ondismiss: () => setProcessing(null) },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp.error?.description || "Payment failed");
        setProcessing(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
      setProcessing(null);
    }
  };

  if (isPremium) {
    return (
      <div
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-semibold"
        title={expiry ? `Premium until ${expiry}` : "Premium active"}
        data-testid={`premium-active-${property.id}`}
      >
        <Crown size={10} /> {lang === "hi" ? "प्रीमियम" : "Premium"} {expiry ? `• ${expiry}` : ""}
      </div>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 text-xs px-2 border-secondary text-secondary-foreground bg-secondary/10 hover:bg-secondary hover:text-secondary-foreground"
        onClick={() => setOpen(true)}
        data-testid={`boost-btn-${property.id}`}
      >
        <Sparkles size={11} className="mr-1" />{lang === "hi" ? "बूस्ट करें" : "Boost"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="boost-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown size={18} className="text-secondary" />
              {lang === "hi" ? "प्रीमियम बूस्ट" : "Boost to Premium"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {lang === "hi"
                ? "इस लिस्टिंग को होमपेज पर लाएँ + वेरिफ़ाइड बैज पाएँ।"
                : "Bring this listing to Homepage + earn a Verified badge."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="p-4 border-2 border-border rounded-lg text-center hover:border-primary transition-colors">
              <div className="text-xs uppercase text-muted-foreground">{lang === "hi" ? "मासिक" : "Monthly"}</div>
              <div className="text-2xl font-bold mt-1">₹299</div>
              <div className="text-[10px] text-muted-foreground">30 {lang === "hi" ? "दिन" : "days"}</div>
              <Button
                className="w-full mt-3 rounded-full bg-primary hover:bg-primary/90"
                size="sm"
                onClick={() => boost("premium_monthly")}
                disabled={processing !== null || !rzpReady}
                data-testid={`boost-monthly-${property.id}`}
              >
                {processing === "premium_monthly" ? <Loader2 className="animate-spin" size={12} /> : (lang === "hi" ? "लें" : "Buy")}
              </Button>
            </div>
            <div className="p-4 border-2 border-secondary rounded-lg text-center bg-secondary/5 relative">
              <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">
                {lang === "hi" ? "बचत 72%" : "Save 72%"}
              </span>
              <div className="text-xs uppercase text-muted-foreground">{lang === "hi" ? "वार्षिक" : "Yearly"}</div>
              <div className="text-2xl font-bold mt-1">₹999</div>
              <div className="text-[10px] text-muted-foreground">365 {lang === "hi" ? "दिन" : "days"}</div>
              <Button
                className="w-full mt-3 rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                size="sm"
                onClick={() => boost("premium_yearly")}
                disabled={processing !== null || !rzpReady}
                data-testid={`boost-yearly-${property.id}`}
              >
                {processing === "premium_yearly" ? <Loader2 className="animate-spin" size={12} /> : (lang === "hi" ? "लें" : "Buy")}
              </Button>
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground text-center mt-3">
            {lang === "hi" ? "Razorpay के जरिए सुरक्षित भुगतान। कोई ऑटो-रिन्यूअल नहीं।" : "Secure payment via Razorpay. No auto-renewal."}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
